import {
  BlotterTable,
  EMPTY_ARRAY,
  getShowJSONContextItem,
  useBehaviorSubject,
  useBlotterTable,
  useCallbackRef,
  useGetDefaultContextMenuItems,
  useJsonModal,
  type CareOrder,
  type MinimalSubscriptionResponse,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useCareOrderNettingPreviewColumns } from './useCareOrderNettingPreviewColumns';

interface PreviewBlotterProps {
  previewWorkingOrder?: CareOrder;
  showAllDecimals: boolean;
}

export function PreviewBlotter({ previewWorkingOrder, showAllDecimals }: PreviewBlotterProps) {
  const rows = useMemo(
    () => (previewWorkingOrder == null ? EMPTY_ARRAY : [previewWorkingOrder]),
    [previewWorkingOrder]
  );
  const columns = useCareOrderNettingPreviewColumns({});
  const { handleClickJson, jsonModal } = useJsonModal<CareOrder>();
  const getDefaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrder>) => {
    const showJsonItem = params?.node?.data ? getShowJSONContextItem({ params, handleClickJson }) : null;
    return compact([showJsonItem, showJsonItem ? 'separator' : null, ...getDefaultContextMenuItems(params)]);
  });

  const { observable: dataObservable } = useBehaviorSubject(
    () =>
      ({
        initial: true,
        data: rows,
      }) satisfies MinimalSubscriptionResponse<CareOrder>,
    [rows]
  );
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );

  const blotterTable = useBlotterTable<CareOrder>({
    dataObservable,
    errorObservable: undefined,
    rowID: 'ClOrdID' satisfies keyof CareOrder,
    columns,
    gridOptions: {
      rowSelection: {
        mode: 'multiRow',
        checkboxes: () => false,
        headerCheckbox: false,
        hideDisabledCheckboxes: true,
        enableClickSelection: false,
        copySelectedRows: true,
      },
      getContextMenuItems,
    },
    exportOptions: {
      name: 'CareOrderNettingPreviewWorkingOrder',
    },
    settings: blotterSettings,
  });

  return (
    <>
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
}
